import React, { createContext, useContext, useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import {
  CompositeDecorator,
  ContentBlock,
  ContentState,
  DraftDecoratorComponentProps,
  Editor,
  EditorState,
  Modifier,
  SelectionState
} from 'draft-js';
import 'draft-js/dist/Draft.css';

import { connect, ResolveThunks } from 'react-redux';
import { RootState } from '~/redux/store';

import Breadcrumbs from '~/components/Breadcrumbs/Breadcrumbs';
import { lookupBuildings, toBuildingOption } from '~/api/building';
import MultiSelect from '~/components/MultiSelect/MultiSelect';
import ImageDropzone from '~/components/ImageDropzone/ImageDropzone';
import GenericModal from '~/components/GenericModal/GenericModal';
import Spinner from '~/components/Spinner/Spinner';
import Switch from 'react-ios-switch';

import { toast } from 'react-toastify';

import { SelectedOptionType } from '~/types/form';
import { SelectType } from '~/enums/SelectType';
import { Roles } from '~/enums/Roles';

import classes from './PostManagement.module.css';
import {
  createPostGroupComment,
  fetchCobuPlusBuildings,
  fetchHomebodyBuildings
} from '~/api/postManagement';

interface DispatchProps {}

type Props = ResolveThunks<DispatchProps> & ReturnType<typeof mapStateToProps>;

// The editor shows a pill; the backend receives the token and swaps in each
// building's name.
const BUILDING_NAME_TOKEN = '[building_name]';
const BUILDING_NAME_ENTITY = 'BUILDING_NAME';
const BUILDING_NAME_PILL_TEXT = 'Building name';
const MAX_MESSAGE_LENGTH = 500;

type RemovePill = (blockKey: string, start: number, end: number) => void;
const RemovePillContext = createContext<RemovePill>(() => {});

const isBuildingNamePill = (contentState: ContentState, entityKey: string) =>
  contentState.getEntity(entityKey).getType() === BUILDING_NAME_ENTITY;

const findBuildingNamePills = (
  block: ContentBlock,
  callback: (start: number, end: number) => void,
  contentState: ContentState
) =>
  block.findEntityRanges((character) => {
    const entityKey = character.getEntity();
    return entityKey !== null && isBuildingNamePill(contentState, entityKey);
  }, callback);

const BuildingNamePill = ({
  blockKey,
  start,
  end,
  children
}: DraftDecoratorComponentProps) => {
  const removePill = useContext(RemovePillContext);
  return (
    <span className={classes.pill}>
      {children}
      <button
        type='button'
        contentEditable={false}
        className={classes.pillRemove}
        aria-label='Remove building name'
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => removePill(blockKey, start, end)}
      >
        ×
      </button>
    </span>
  );
};

const decorator = new CompositeDecorator([
  { strategy: findBuildingNamePills, component: BuildingNamePill }
]);

const emptyEditor = () => EditorState.createEmpty(decorator);

const toPostText = (contentState: ContentState) =>
  contentState
    .getBlocksAsArray()
    .map((block) => {
      const text = block.getText();
      let postText = '';
      let cursor = 0;
      findBuildingNamePills(
        block,
        (start, end) => {
          postText += text.slice(cursor, start) + BUILDING_NAME_TOKEN;
          cursor = end;
        },
        contentState
      );
      return postText + text.slice(cursor);
    })
    .join('\n');

const PostManagement = (props: Props) => {
  const { userRole } = props;
  const canPinOrDisableReplies =
    userRole === Roles.Vcm || userRole === Roles.Superadmin;

  const [editorState, setEditorState] = useState(emptyEditor);
  const message = toPostText(editorState.getCurrentContent());
  const [image, setImage] = useState<File | string>('');
  const [multiValue, setMultiValue] = useState<SelectedOptionType[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [buildingsWithOutVCM, setBuildingsWithOutVCM] = useState<string[]>([]);
  const [isPinnedMessage, setIsPinnedMessage] = useState(false);
  const [isDisabledReplies, setIsDisabledReplies] = useState(false);

  const [showFormLoader, setShowFormLoader] = useState(false);
  const [pasteBuildingsText, setPasteBuildingsText] = useState('');

  const insertBuildingName = () => {
    const content = editorState
      .getCurrentContent()
      .createEntity(BUILDING_NAME_ENTITY, 'IMMUTABLE');
    const withPill = Modifier.replaceText(
      content,
      editorState.getSelection(),
      BUILDING_NAME_PILL_TEXT,
      undefined,
      content.getLastCreatedEntityKey()
    );
    // A trailing space keeps the caret outside the pill so typing continues
    // as plain text.
    const withSpace = Modifier.insertText(
      withPill,
      withPill.getSelectionAfter(),
      ' '
    );
    setEditorState(
      EditorState.forceSelection(
        EditorState.push(editorState, withSpace, 'insert-characters'),
        withSpace.getSelectionAfter()
      )
    );
  };

  const removePill: RemovePill = (blockKey, start, end) =>
    setEditorState((current) => {
      const pillRange = SelectionState.createEmpty(blockKey).merge({
        anchorOffset: start,
        focusOffset: end
      });
      const content = Modifier.removeRange(
        current.getCurrentContent(),
        pillRange,
        'backward'
      );
      return EditorState.push(current, content, 'remove-range');
    });

  const exceedsMaxLength = (addedText: string) =>
    editorState.getCurrentContent().getPlainText().length + addedText.length >
    MAX_MESSAGE_LENGTH;

  const { data: cobuPlusBuildings, isLoading: isCobuPlusLoading } = useQuery(
    ['cobu-plus-buildings'],
    () => fetchCobuPlusBuildings()
  );

  const { data: homebodyBuildings, isLoading: isHomebodyLoading } = useQuery(
    ['homebody-buildings'],
    () => fetchHomebodyBuildings()
  );

  const isLoading = isCobuPlusLoading || isHomebodyLoading;

  const handlePasteBuildings = async (
    event: React.ClipboardEvent<HTMLTextAreaElement>
  ) => {
    event.preventDefault();
    const pastedText = event.clipboardData.getData('text');

    let tokens = pastedText.split('\n');
    if (tokens.length === 1) {
      tokens = pastedText.split(',');
    }
    tokens = tokens.map((token) => token.trim()).filter((token) => token);

    if (!tokens.length) return;
    const found = await lookupBuildings({ names: tokens });
    const matched: SelectedOptionType[] = found.map(toBuildingOption);
    const foundNames = new Set(found.map((b) => b.name.toLowerCase()));
    const unmatched = tokens.filter(
      (token) => !foundNames.has(token.toLowerCase())
    );

    const existingValues = new Set(
      multiValue.map((building) => building.value)
    );
    const newMatches = matched.filter(
      (building) => !existingValues.has(building.value)
    );

    if (newMatches.length > 0) {
      setMultiValue([...multiValue, ...newMatches]);
    }

    if (unmatched.length > 0) {
      toast(`Could not match building(s): ${unmatched.join(', ')}`);
    }

    setPasteBuildingsText('');
  };

  const handleDrop = (acceptedFiles: File[]) => {
    setImage(acceptedFiles[0]);
  };

  const onCancel = () => {
    setEditorState(emptyEditor());
    setImage('');
    setIsDisabledReplies(false);
    setIsPinnedMessage(false);
  };

  const createTemplateMutation = useMutation(createPostGroupComment, {
    onSuccess: (response) => {
      const countPostBuilding = response.data?.countBuildingsPostMessage;
      const listOfBuildingsWithoutVCM =
        response.data?.buildingWithOutVCMCommenter || null;

      if (countPostBuilding > 0) {
        const extraMessage =
          listOfBuildingsWithoutVCM && listOfBuildingsWithoutVCM.length > 0
            ? ' Check the list of buildings without VCM Commenter.'
            : '';
        toast(
          `Post for ${countPostBuilding} buildings were created successfully.${extraMessage}`
        );
      } else
        toast(
          `This message was not send to any building! Check the list of buildings without VCM Commenter`
        );

      setEditorState(emptyEditor());
      setMultiValue([]);
      setImage('');
      setShowFormLoader(false);
      setIsPinnedMessage(false);
      setIsDisabledReplies(false);

      if (listOfBuildingsWithoutVCM && listOfBuildingsWithoutVCM.length > 0)
        setBuildingsWithOutVCM(listOfBuildingsWithoutVCM);
    },
    onError: (e: any) => {
      setShowFormLoader(false);
      toast(`There was an error creating the post: ${e.message || ''}`);
    }
  });

  const onSubmit = () => {
    setBuildingsWithOutVCM([]);
    const formData = new FormData();
    const buildingIds = multiValue.map((building) => building.value);

    formData.append(
      'postData',
      JSON.stringify({
        comment: message,
        buildingIds,
        isPinnedMessage,
        isDisabledReplies
      })
    );

    if (image) {
      formData.append('image', image);
    }

    setShowFormLoader(true);
    createTemplateMutation.mutate(formData);
  };

  const preSubmitButton = () => {
    if (!multiValue || multiValue.length <= 0) {
      toast('Buildings must be selected for posting');
      return;
    } else if (!message) {
      toast('You must specify a text for the post!');
      return;
    }
    setShowModal(true);
  };

  if (isLoading) {
    return <Spinner />;
  }
  return (
    <div className='content-container'>
      <div className='float-spinner-parent'>
        <form>
          {showFormLoader && <Spinner float />}
          <div>
            <Breadcrumbs
              indexesToHide={canPinOrDisableReplies ? undefined : [1]}
            />
            <div className={classes.container}>
              <h1 className='h1-title'>Post Management</h1>
              <MultiSelect
                asyncBuildings
                selectType={SelectType.AutoAdjutForm}
                multiValue={multiValue}
                setMultiValue={setMultiValue}
                placeholder='Select buildings...'
                cobuPlusBuildings={cobuPlusBuildings?.data || []}
                homebodyBuildings={homebodyBuildings?.data || []}
                defaultOptions={
                  canPinOrDisableReplies
                    ? undefined
                    : [{ value: 'all', label: 'All Buildings' }]
                }
              />
              <textarea
                id='pasteBuildings'
                name='pasteBuildings'
                value={pasteBuildingsText}
                onChange={(e) => setPasteBuildingsText(e.target.value)}
                onPaste={handlePasteBuildings}
                placeholder={
                  'Paste a list of building names (one per line) to add them to the To field...'
                }
                style={{ marginTop: '8px', minHeight: '48px' }}
              />
              <div style={{ marginTop: '20px', marginBottom: '20px' }}>
                <ImageDropzone image={image} handleDrop={handleDrop} />
              </div>
              <div
                className='display-flex'
                style={{ alignItems: 'center', marginBottom: '8px' }}
              >
                <button
                  type='button'
                  className='btn-standard btn-seconday'
                  onClick={insertBuildingName}
                >
                  + Building name
                </button>
                <p className='margin-left-16'>
                  Adds a Building name tag at your cursor. Each building sees
                  its own name there. Example: &quot;Happening today at
                  [Building name]&quot; posts as &quot;Happening today at 100
                  West Chestnut&quot;.
                </p>
              </div>
              <div className={classes.messageEditor}>
                <RemovePillContext.Provider value={removePill}>
                  <Editor
                    editorState={editorState}
                    onChange={setEditorState}
                    placeholder='Message...'
                    handleBeforeInput={(chars) =>
                      exceedsMaxLength(chars) ? 'handled' : 'not-handled'
                    }
                    handlePastedText={(text) =>
                      exceedsMaxLength(text) ? 'handled' : 'not-handled'
                    }
                  />
                </RemovePillContext.Provider>
              </div>
            </div>
            <div className={classes.bottomContainer}>
              {canPinOrDisableReplies && (
                <div className={classes.togglesContainer}>
                  <div className={`display-flex`}>
                    <div className={classes.toggleContainer}>
                      <Switch
                        checked={isPinnedMessage}
                        onChange={(newSwitchState: boolean) => {
                          setIsPinnedMessage(newSwitchState);
                          if (!newSwitchState) {
                            setIsDisabledReplies(false);
                          }
                        }}
                        disabled={isLoading}
                      />
                      <div className='margin-left-16'>
                        <p>Pin post</p>
                      </div>
                    </div>
                  </div>
                  <div className={`display-flex`}>
                    <div className={classes.toggleContainer}>
                      <Switch
                        checked={isDisabledReplies}
                        onChange={(newSwitchState: boolean) => {
                          setIsDisabledReplies(newSwitchState);
                        }}
                        disabled={isLoading || !isPinnedMessage}
                      />
                      <div className='margin-left-16'>
                        <p>Disable replies</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <div className={classes.buttonsContainer}>
                <button
                  type='button'
                  className='btn-standard btn-primary margin-right-16'
                  onClick={preSubmitButton}
                >
                  Submit
                </button>
                <button
                  className='btn-standard btn-seconday weight-500'
                  type='button'
                  onClick={onCancel}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
      <GenericModal
        showModal={showModal}
        title=''
        applyModalStretch={true}
        content={
          <div style={{ margin: '0 10px', paddingTop: '40px' }}>
            <h3 className={classes.contentBodyh3}>
              Really want to post to all selected buildings? This action cannot
              be undone
            </h3>
          </div>
        }
        actionText={'Submit'}
        actionFunction={() => {
          onSubmit();
          setShowModal(false);
        }}
        onChange={() => {
          setEditorState(emptyEditor());
          setShowModal(false);
        }}
      />
      {buildingsWithOutVCM && buildingsWithOutVCM.length > 0 && (
        <div className={classes.container}>
          <h3 className={classes.contentBodyh3}>
            Message cannot be sent to the following buildings because the VCM
            Commenter is not set:
          </h3>
          <ul>
            {buildingsWithOutVCM.map((building) => (
              <li key={building}>{building}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

function mapStateToProps(state: RootState) {
  return {
    userRole: state.auth.userRole
  };
}

export default connect(mapStateToProps)(PostManagement);
